import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { UserRole } from '../contexts/AuthContext';
import { useApprovalCounts } from '../contexts/ApprovalCountsContext';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  Phone,
  Building2,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  getTenantRequests,
  approveTenantRequest,
  rejectTenantRequest,
} from '../services/auth.service';
import { PageTransition } from '../components/PageTransition';
import { getRoleLabel } from '../lib/role-labels';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

interface TenantRequest {
  id: number;
  name: string;
  email: string;
  phone?: string;
  note?: string;
  suggestedTenantName: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  likelyExisting: boolean;
  createdAt: string;
  tenantId?: number;
}

interface TenantCreateFormData {
  name: string;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  emailFatturazione: string;
  language: 'en' | 'it';
  vatNumber?: string;
  pec?: string;
  companyAddress?: string;
}

type ApprovalAction = 'select' | 'associate' | 'create';
type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type RequestStatusFilter = 'ALL' | RequestStatus;

const normalizeStatus = (status: string): RequestStatus => {
  const normalized = status.toUpperCase();
  if (normalized === 'APPROVED') return 'APPROVED';
  if (normalized === 'REJECTED') return 'REJECTED';
  return 'PENDING';
};

const normalizeStatusFilter = (value: string): RequestStatusFilter => {
  const normalized = value.trim().toUpperCase();
  if (normalized === 'APPROVED') return 'APPROVED';
  if (normalized === 'REJECTED') return 'REJECTED';
  if (normalized === 'PENDING') return 'PENDING';
  return 'ALL';
};

const AdminTenantRequests: React.FC = () => {
  const { t } = useTranslation();
  const { refreshCounts } = useApprovalCounts();
  const [requests, setRequests] = useState<TenantRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<RequestStatusFilter>('PENDING');
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TenantRequest | null>(null);
  const [approvalAction, setApprovalAction] = useState<ApprovalAction>('select');
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [createTenantFormData, setCreateTenantFormData] = useState<TenantCreateFormData>({
    name: '',
    logoUrl: '',
    primaryColor: '#f97316',
    secondaryColor: '#ea580c',
    emailFatturazione: '',
    language: 'it',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.OPERATOR);

  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTenantRequests();
      const normalizedRequests = (data as TenantRequest[]).map((request) => ({
        ...request,
        status: normalizeStatus(request.status),
      }));
      setRequests(normalizedRequests);
      setError('');
    } catch (err) {
      setError(t('pages.adminTenantRequests.errors.fetch', 'Failed to load tenant requests'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tenants`);
      setTenants(response.data);
    } catch (err) {
      console.error('Failed to load tenants');
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchTenants();
  }, [fetchRequests, fetchTenants]);

  const handleApproveClick = (request: TenantRequest) => {
    setSelectedRequest(request);
    setApprovalAction('select');
    setSelectedTenantId('');
    setSelectedRole(UserRole.OPERATOR);
    setCreateTenantFormData({
      name: request.suggestedTenantName || '',
      logoUrl: '',
      primaryColor: '#f97316',
      secondaryColor: '#ea580c',
      emailFatturazione: request.email || '',
      language: 'it',
      vatNumber: '',
      pec: '',
      companyAddress: '',
    });
    setApprovalDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    if (approvalAction === 'select') return;

    try {
      setIsProcessing(true);
      await approveTenantRequest(selectedRequest.id, approvalAction, {
        tenantId: approvalAction === 'associate' ? parseInt(selectedTenantId, 10) : undefined,
        name: createTenantFormData.name,
        emailFatturazione: createTenantFormData.emailFatturazione,
        primaryColor: createTenantFormData.primaryColor,
        secondaryColor: createTenantFormData.secondaryColor,
        language: createTenantFormData.language,
        logoUrl: createTenantFormData.logoUrl,
        role: selectedRole,
        vatNumber: createTenantFormData.vatNumber,
        pec: createTenantFormData.pec,
        companyAddress: createTenantFormData.companyAddress,
      });
      setApprovalDialog(false);
      await fetchRequests();
      refreshCounts();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        t('pages.adminTenantRequests.errors.approve', 'Failed to approve request')
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectClick = (request: TenantRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialog(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      setIsProcessing(true);
      await rejectTenantRequest(selectedRequest.id, rejectionReason);
      setRejectDialog(false);
      await fetchRequests();
      refreshCounts();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        t('pages.adminTenantRequests.errors.reject', 'Failed to reject request')
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredRequests = requests.filter((r) => {
    const normalizedFilter = normalizeStatusFilter(statusFilter);
    if (normalizedFilter === 'ALL') return true;
    return normalizeStatus(String(r.status || '')) === normalizedFilter;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <Badge variant="outline" className="bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800">
            <Clock className="mr-1 h-3 w-3" />
            {t('pages.adminTenantRequests.status.pending', 'Pending')}
          </Badge>
        );
      case 'APPROVED':
        return (
          <Badge className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200">
            <CheckCircle className="mr-1 h-3 w-3" />
            {t('pages.adminTenantRequests.status.approved', 'Approved')}
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge variant="outline" className="bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-200 border-red-200 dark:border-red-800">
            <XCircle className="mr-1 h-3 w-3" />
            {t('pages.adminTenantRequests.status.rejected', 'Rejected')}
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <PageTransition>
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('pages.adminTenantRequests.title', 'Tenant Requests')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('pages.adminTenantRequests.subtitle', 'Review and approve new tenant registration requests')}
          </p>
        </div>
        <div className="w-full sm:w-56">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(normalizeStatusFilter(v))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t('pages.adminTenantRequests.filterAll', 'All Status')}</SelectItem>
              <SelectItem value="PENDING">{t('pages.adminTenantRequests.filterPending', 'Pending')}</SelectItem>
              <SelectItem value="APPROVED">{t('pages.adminTenantRequests.filterApproved', 'Approved')}</SelectItem>
              <SelectItem value="REJECTED">{t('pages.adminTenantRequests.filterRejected', 'Rejected')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">
                {t('pages.adminTenantRequests.empty', 'No tenant requests found')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {filteredRequests.map((request) => {
            const requestStatus = normalizeStatus(request.status);
            const isPending = requestStatus === 'PENDING';

            return (
              <Card
                key={request.id}
                className="transition-colors hover:bg-secondary/10"
              >
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold">{request.name}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{request.email}</span>
                        </p>
                        {request.phone && (
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {request.phone}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(requestStatus)}
                        <p className="text-xs text-muted-foreground">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>
                        {t('pages.adminTenantRequests.suggestedTenant', 'Suggested:')}{' '}
                        <span className="font-medium text-foreground">{request.suggestedTenantName}</span>
                      </p>
                      {request.note && (
                        <p className="rounded border bg-background p-2 text-xs text-muted-foreground">
                          {request.note}
                        </p>
                      )}
                      {request.likelyExisting && (
                        <Badge variant="outline" className="bg-orange-50 dark:bg-orange-950">
                          {t('pages.adminTenantRequests.likelyExisting', 'Likely Existing Company')}
                        </Badge>
                      )}
                    </div>

                    {isPending && (
                      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproveClick(request);
                          }}
                          size="sm"
                          variant="default"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : null}
                          {t('pages.adminTenantRequests.actions.approve', 'Approve')}
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRejectClick(request);
                          }}
                          size="sm"
                          variant="destructive"
                          disabled={isProcessing}
                        >
                          {isProcessing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : null}
                          {t('pages.adminTenantRequests.actions.reject', 'Reject')}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('pages.adminTenantRequests.approveDialog.title', 'Approve Request')}
            </DialogTitle>
            <DialogDescription>
              {t('pages.adminTenantRequests.approveDialog.description', 'Choose how to proceed with this request')}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-background p-3">
                <p className="text-sm font-medium">{selectedRequest.name}</p>
                <p className="text-xs text-muted-foreground">{selectedRequest.email}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="action">{t('pages.adminTenantRequests.approveDialog.action', 'Action')}</Label>
                <Select value={approvalAction} onValueChange={(v) => setApprovalAction(v as ApprovalAction)}>
                  <SelectTrigger id="action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select">
                      {t('pages.adminTenantRequests.approveDialog.select', 'Select')}
                    </SelectItem>
                    <SelectItem value="associate">
                      {t('pages.adminTenantRequests.approveDialog.associate', 'Associate with Existing Tenant')}
                    </SelectItem>
                    <SelectItem value="create">
                      {t('pages.adminTenantRequests.approveDialog.create', 'Create New Tenant')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {approvalAction !== 'select' && (
                <div className="space-y-2">
                  <Label htmlFor="role">{t('pages.usersManager.columns.role', 'Role')}</Label>
                  <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as UserRole)}>
                    <SelectTrigger id="role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(UserRole).filter((r) => r !== UserRole.SUPER_ADMIN).map((role) => (
                        <SelectItem key={role} value={role}>
                          {getRoleLabel(role, t)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {approvalAction === 'associate' && (
                <div className="space-y-2">
                  <Label htmlFor="tenant">
                    {t('pages.adminTenantRequests.approveDialog.selectTenant', 'Select Tenant')}
                  </Label>
                  <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                    <SelectTrigger id="tenant">
                      <SelectValue placeholder="Choose a tenant..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map((t) => (
                        <SelectItem key={t.id} value={t.id.toString()}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {approvalAction === 'create' && (
                <div className="space-y-4 border rounded-md p-3">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">{t('pages.tenants.columns.name', 'Name')}</Label>
                    <Input
                      id="create-name"
                      value={createTenantFormData.name}
                      onChange={(e) =>
                        setCreateTenantFormData({ ...createTenantFormData, name: e.target.value })
                      }
                      placeholder={t('pages.tenants.namePlaceholder', 'Acme Corporation')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="create-logo">{t('pages.tenants.columns.logoUrl', 'Logo URL')}</Label>
                    <Input
                      id="create-logo"
                      type="url"
                      value={createTenantFormData.logoUrl}
                      onChange={(e) =>
                        setCreateTenantFormData({ ...createTenantFormData, logoUrl: e.target.value })
                      }
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="create-primaryColor">
                        {t('pages.tenants.columns.primaryColor', 'Primary Color')}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="create-primaryColor"
                          type="color"
                          value={createTenantFormData.primaryColor}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              primaryColor: e.target.value,
                            })
                          }
                          className="h-10 w-14 cursor-pointer p-1"
                        />
                        <Input
                          value={createTenantFormData.primaryColor}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              primaryColor: e.target.value,
                            })
                          }
                          className="flex-1 font-mono text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="create-secondaryColor">
                        {t('pages.tenants.columns.secondaryColor', 'Secondary Color')}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="create-secondaryColor"
                          type="color"
                          value={createTenantFormData.secondaryColor}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              secondaryColor: e.target.value,
                            })
                          }
                          className="h-10 w-14 cursor-pointer p-1"
                        />
                        <Input
                          value={createTenantFormData.secondaryColor}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              secondaryColor: e.target.value,
                            })
                          }
                          className="flex-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="create-email">
                      {t('pages.tenants.columns.email', 'Billing Email')}
                    </Label>
                    <Input
                      id="create-email"
                      type="email"
                      value={createTenantFormData.emailFatturazione}
                      onChange={(e) =>
                        setCreateTenantFormData({
                          ...createTenantFormData,
                          emailFatturazione: e.target.value,
                        })
                      }
                      placeholder="billing@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('pages.tenants.columns.language', 'Language')}</Label>
                    <Select
                      value={createTenantFormData.language}
                      onValueChange={(value: 'en' | 'it') =>
                        setCreateTenantFormData({ ...createTenantFormData, language: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="it">Italiano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                    <div className="border-t pt-4 mt-4 space-y-4">
                      <h4 className="font-semibold text-sm">{t('pages.tenants.form.commercialFields', 'Commercial Registry')}</h4>
                      <p className="text-xs text-muted-foreground">
                        {t('pages.tenants.form.commercialFieldsDescription', 'Link this tenant to the commercial registry')}
                      </p>

                      <div className="space-y-2">
                        <Label htmlFor="create-vat">{t('pages.tenants.form.vatNumber', 'VAT Number')}</Label>
                        <Input
                          id="create-vat"
                          value={createTenantFormData.vatNumber || ''}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              vatNumber: e.target.value,
                            })
                          }
                          placeholder={t('pages.tenants.form.vatNumberPlaceholder', 'e.g. IT12345678901')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="create-pec">{t('pages.tenants.form.pec', 'PEC')}</Label>
                        <Input
                          id="create-pec"
                          type="email"
                          value={createTenantFormData.pec || ''}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              pec: e.target.value,
                            })
                          }
                          placeholder={t('pages.tenants.form.pecPlaceholder', 'e.g. company@pec.it')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="create-address">{t('pages.tenants.form.companyAddress', 'Company Address')}</Label>
                        <Input
                          id="create-address"
                          value={createTenantFormData.companyAddress || ''}
                          onChange={(e) =>
                            setCreateTenantFormData({
                              ...createTenantFormData,
                              companyAddress: e.target.value,
                            })
                          }
                          placeholder={t('pages.tenants.form.companyAddressPlaceholder', 'Street, City, ZIP, Country')}
                        />
                      </div>
                    </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleApprove}
              disabled={
                isProcessing ||
                approvalAction === 'select' ||
                (approvalAction === 'associate' && !selectedTenantId) ||
                (approvalAction === 'create' &&
                  (!createTenantFormData.name.trim() || !createTenantFormData.emailFatturazione.trim()))
              }
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('pages.adminTenantRequests.approveDialog.approve', 'Approve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('pages.adminTenantRequests.rejectDialog.title', 'Reject Request')}
            </DialogTitle>
            <DialogDescription>
              {t('pages.adminTenantRequests.rejectDialog.description', 'Provide a reason for rejection')}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-background p-3">
                <p className="text-sm font-medium">{selectedRequest.name}</p>
                <p className="text-xs text-muted-foreground">{selectedRequest.email}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">
                  {t('pages.adminTenantRequests.rejectDialog.reason', 'Reason')}
                </Label>
                <Textarea
                  id="reason"
                  placeholder={t(
                    'pages.adminTenantRequests.rejectDialog.reasonPlaceholder',
                    'Enter reason for rejection...'
                  )}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isProcessing || !rejectionReason.trim()}
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('pages.adminTenantRequests.rejectDialog.reject', 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageTransition>
  );
};

export default AdminTenantRequests;
